import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageCircleQuestion, Copy, Mail, Clock, AlertTriangle, Sparkles } from "lucide-react";

/**
 * Admin-only page: compiled list of every team's questions for their business,
 * grouped by team letter. One-click "Copy for email" per team so admin can
 * paste straight into an email to the sponsor. Sits on top of the existing
 * GET /api/admin/team-questions/export endpoint.
 */
export default function AdminTeamQuestions() {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [programme, setProgramme] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, p] = await Promise.all([
        api.get("/admin/team-questions/export"),
        api.get("/programme").then((r) => r.data).catch(() => ({})),
      ]);
      setPayload(data);
      setProgramme(p || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load compiled questions");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const buildEmailText = (team) => {
    const lines = [];
    lines.push(`Hi ${team.business_name ? team.business_name + " team" : "team"},`);
    lines.push("");
    lines.push(`Following Session 2, Team ${team.team_letter || ""} (${team.team_name || "unnamed"}) has compiled the following questions:`);
    lines.push("");
    (team.questions || []).forEach((q, i) => {
      lines.push(`${i + 1}. ${q.question}${q.is_sensitive ? "  [COMMERCIALLY SENSITIVE — please treat as confidential]" : ""}`);
    });
    lines.push("");
    lines.push("Many thanks in advance for your time — please reply at your convenience.");
    lines.push("");
    lines.push("Best regards,");
    lines.push("The iTeams Programme Team · The Weave × University of Essex");
    return lines.join("\n");
  };

  const copyForEmail = async (team) => {
    const text = buildEmailText(team);
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${team.questions?.length || 0} question(s) for Team ${team.team_letter}`);
    } catch {
      toast.error("Could not copy — select and copy manually below");
    }
  };

  const mailto = (team) => {
    const subject = `iTeams — Follow-up questions from Team ${team.team_letter || ""}`;
    const body = buildEmailText(team);
    // Use an anchor with mailto so client mail apps open reliably
    const href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  };

  const deadlineDate = programme?.questions_deadline ? new Date(programme.questions_deadline) : null;
  const passed = deadlineDate && deadlineDate.getTime() < Date.now();

  const teams = payload?.teams || [];
  const totalQuestions = teams.reduce((n, t) => n + (t.questions?.length || 0), 0);
  const sensitiveCount = teams.reduce(
    (n, t) => n + (t.questions || []).filter((q) => q.is_sensitive).length, 0,
  );

  return (
    <div className="space-y-8" data-testid="admin-team-questions-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><MessageCircleQuestion size={12} /> Admin · Team questions</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2 text-[#0F1F2E]">Compiled questions for the businesses</h1>
          <p className="text-sm text-[#525252] mt-2 max-w-2xl">
            One list per team, grouped and ready to email to each sponsor. Copy the block or open your mail
            client with the message pre-filled. Sensitive items are flagged for you.
          </p>
        </div>
        {deadlineDate && (
          <div className={`text-xs px-3 py-2 border ${passed ? "border-[#A23232] bg-[#FDEDED] text-[#7A1F1F]" : "border-[#f4b847] bg-[#FEF9E7] text-[#7A5B17]"}`} data-testid="admin-team-questions-deadline">
            <div className="flex items-center gap-1.5 label-eyebrow"><Clock size={11} /> {passed ? "Deadline passed" : "Deadline"}</div>
            <div className="font-display text-lg font-black tracking-tight">
              {deadlineDate.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        )}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t border-[#E5E7EB]" data-testid="admin-team-questions-stats">
        <Stat label="Teams with questions" value={teams.length} accent="#25AAAA" />
        <Stat label="Total questions" value={totalQuestions} accent="#2596be" />
        <Stat label="Commercially sensitive" value={sensitiveCount} accent="#A23232" />
      </div>

      {/* Body */}
      {loading ? (
        <div className="text-sm text-[#525252]">Loading…</div>
      ) : teams.length === 0 ? (
        <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-10 text-center" data-testid="admin-team-questions-empty">
          <Sparkles size={20} className="text-[#25AAAA] mx-auto" />
          <div className="font-display text-2xl font-black tracking-tighter mt-3 text-[#0F1F2E]">Nothing to send yet</div>
          <p className="text-sm text-[#525252] mt-2 max-w-md mx-auto">
            No team has added a question yet. Once teams start compiling follow-ups on their <em>/my-team</em>
            page, they&apos;ll appear here grouped and ready to email.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {teams.map((t) => {
            const testKey = t.team_letter || (t.team_id ? t.team_id.slice(0, 8) : "unknown");
            return (
            <div key={t.team_id} className="border border-[#0A0A0A] bg-white" data-testid={`admin-team-questions-team-${testKey}`}>
              {/* Team header */}
              <div className="flex items-start justify-between gap-3 flex-wrap border-b border-[#0A0A0A] bg-[#0F1F2E] text-white p-5">
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#F4B847]">
                    Team {t.team_letter || "—"} · {t.questions?.length || 0} question{(t.questions?.length || 0) === 1 ? "" : "s"}
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl font-black tracking-tighter mt-1">
                    {t.team_name || "Unnamed team"}
                  </h2>
                  <div className="text-xs text-neutral-300 mt-1">
                    Business: <strong className="text-white">{t.business_name || "Not assigned yet"}</strong>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    onClick={() => copyForEmail(t)}
                    className="rounded-none bg-white text-[#0F1F2E] border-white hover:bg-[#F4B847] hover:border-[#F4B847]"
                    data-testid={`admin-team-questions-copy-${testKey}`}
                  >
                    <Copy size={14} className="mr-1.5" /> Copy for email
                  </Button>
                  <Button
                    onClick={() => mailto(t)}
                    className="rounded-none bg-[#25AAAA] hover:bg-[#1e7d7d]"
                    data-testid={`admin-team-questions-mailto-${testKey}`}
                  >
                    <Mail size={14} className="mr-1.5" /> Open mail draft
                  </Button>
                </div>
              </div>

              {/* Questions list */}
              <ol className="divide-y divide-[#E5E7EB]">
                {(t.questions || []).map((q, i) => (
                  <li key={q.id} className="p-5 flex items-start gap-3" data-testid={`admin-team-questions-question-${q.id}`}>
                    <div className="font-display text-3xl font-black tracking-tighter text-[#25AAAA] w-10 flex-shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {q.is_sensitive && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#A23232] text-white px-1.5 py-0.5 font-bold">
                            <AlertTriangle size={9} /> Sensitive
                          </span>
                        )}
                        <p className="text-sm text-[#0A0A0A] leading-relaxed">{q.question}</p>
                      </div>
                      <div className="text-[11px] uppercase tracking-wider text-[#525252] mt-2">
                        {q.author_name || "Team member"} · {new Date(q.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </div>
                      {q.ai_check_result && (
                        <div className="text-[11px] text-[#525252] mt-1 italic">AI check: {q.ai_check_result}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="border-r border-b border-[#E5E7EB] bg-white p-5" style={{ borderTopColor: accent, borderTopWidth: 2 }}>
      <div className="label-eyebrow">{label}</div>
      <div className="font-display text-4xl font-black tracking-tighter mt-2 text-[#0F1F2E]" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}
